import { Component, ElementRef, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AbstractComponent, Company, ConfirmationComponent, FirebaseService, RoleUserEnum, UploadTypeEnum, User, UserService, UtilHelper } from 'lib-trend-core';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'app-user-form',
    // templateUrl: './user-form.component.html',
    templateUrl: './user-form.v2.component.html',
    // styleUrl: './user-form.component.scss',
    styleUrl: './user-form.v2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserFormComponent extends AbstractComponent {
  @ViewChild('picker', { static: true }) picker: MatDatepicker<Date>;
  @ViewChild('fileInputFile', { static: false }) inputfile: ElementRef;
  @ViewChild('slideToggleAdmin') slideToggleAdmin: MatSlideToggle;
  @ViewChild('slideToggleSupervisor') slideToggleSupervisor: MatSlideToggle;
  @ViewChild('slideToggleAttendant') slideToggleAttendant: MatSlideToggle;

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  user: User = {} as User;
  file: File;
  bytes: string;

  breadcrumb = [
    { href: '/user', label: 'user.breadcrumb.users' },
    { label: 'user.breadcrumb.addUser' },
  ]

  constructor(
    injector: Injector,
    public firebaseService: FirebaseService,
    public userService: UserService,
    public dialog: MatDialog,
  ) {
    super(injector);
    this.createForm();
  }

  async ngOnInit() {
    const userId = this.getParam("id");
    this.isNew = !userId;

    if (!this.isNew) {
      this.breadcrumb[1].label = this.t('user.form.breadcrumbEdit');
    }

    if (userId) {
      this.userService.getById(userId).subscribe(user => {
        this.user = user;
        if (this.user) {
          this.formGroup.patchValue({
            ...this.user,
            roleAdmin: this.user.roles.includes(RoleUserEnum.ADMIN),
            roleSupervisor: this.user.roles.includes(RoleUserEnum.SUPERVISOR),
            roleAttendant: this.user.roles.includes(RoleUserEnum.ATTENDANT)
          });
        };
      });
    };
  }

  /***/
  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      _id: [null, []],
      name: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
      email: [null, [Validators.required, Validators.pattern(this.emailRegex)]],
      phone: [null, [Validators.required,]],
      tin: [null, []],
      birthDate: [null, []],
      image: [null, []],
      signatureConversation: [true, []],
      roleAdmin: [false, []],
      roleSupervisor: [false, []],
      roleAttendant: [false, []],
      addLeads: [true, []],
      clientVisibilitys: [false, []],
      deleteMessage: [false, []],
      editMessage: [false, []],
      blockContact: [true, []],
      viewIntegrations: [false, []],
      exportConversation: [false, []],
      viewContacts: [true, []],
      viewFullHistory: [true, []],
      // RF-22 · ADR-003: newly created users default to owner-restricted, mirroring
      // `User.viewAllDeals` (user.entity.ts → `default: false`). Defaulting to true
      // here gave the same role different board visibility depending on whether the
      // user was created through this form or through any other path — and, because
      // `patchValue` skips keys absent from the loaded user, it silently granted
      // view-all on the first edit of a user that predated the field. Deploy-day
      // behaviour is preserved by scripts/backfill-lead-radar-defaults.js, which
      // sets true for users that ALREADY existed — not by this default.
      viewAllDeals: [false, []],
      transferAttendances: [true, []],
    });
  }

  /***/
  addImage() {
    this.inputfile.nativeElement.click();
  }

  /***/
  back(): void {
    history.back();
  }

  userImage() {
    const imageUser = this.user.image;

    if (imageUser) {
      return imageUser;
    } else {
      return UtilHelper.no_image;
    }
  }

  save() {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    };

    const roles = [];
    if (this.formGroup.value.roleAdmin) roles.push(RoleUserEnum.ADMIN);
    if (this.formGroup.value.roleSupervisor) roles.push(RoleUserEnum.SUPERVISOR);
    if (this.formGroup.value.roleAttendant) roles.push(RoleUserEnum.ATTENDANT);

    const user = { ...this.formGroup.value, roles: roles };
    delete user.roleAdmin;
    delete user.roleAttendant;
    delete user.roleSupervisor;

    user.company = <Company>{ _id: super.getIDCurrentCompany() };

    if (this.isNew) {
      this.userService.create(user).subscribe({
        next: (value) => this.alertService.success(),
        error: (err) => this.alertService.error(err.error.message),
        complete: () => {
          this.back();
          this.loading = false;
        }
      });
    } else {
      this.userService.update(this.user._id, user).subscribe({
        next: (value) => this.alertService.success(),
        error: (err) => this.alertService.error(err.error.message),
        complete: () => {
          this.back();
          this.loading = false;
        }
      });
    }
  }

  deleteUser(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, { width: '500px' });
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');

      dialogRef.afterClosed().subscribe((result) => {
        if (Boolean(result) === true) {
          this.userService.delete(id).subscribe({
            next: () => {
              this.alertService.success();
              this.router.navigate(['/user']);
            },
            error: (err) => this.alertService.error(err.error.message),
          });
        };
      });
    });
  };

  openFileBrowser(event: any) {
    event.preventDefault();
    const element: HTMLElement = document.getElementById('dropzone-file') as HTMLElement;
    element.click();
  }

  onSelectImagem(event) {
    const target = event.target;
    const files: FileList = target.files;

    if (files[0].size > 1000000) { // 1 MB
      this.alertService.warning(this.t('user.form.imageSizeError'));
      return;
    }

    if (!(files[0].type === 'image/jpeg' || files[0].type === 'image/png')) {
      this.alertService.warning(this.t('user.form.imageFormatError'));
      return;
    }

    this.file = files[0];

    let reader = new FileReader();
    reader.onload = async (file) => {
      if (this.file) {
        this.loadingSpinnerSubject.next(true);
        const dateMilisecond = new Date().getTime();
        const filename: string = dateMilisecond.toString();

        this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.USER).then((snapshot: { url: string }) => {
          this.loadingSpinnerSubject.next(false);
          this.user.image = snapshot.url;
          this.formGroup.patchValue({ image: snapshot.url });
        }).catch((error) => this.alertService.error(this.t('user.form.uploadError')));
      }
    };
    reader.readAsArrayBuffer(this.file);
  }

  handleEnterKey(event: KeyboardEvent, controlName: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const control = this.formGroup.get(controlName);
      const current = control?.value;
      control?.setValue(!current);
    }
  }

  toogleRoles(event: Event, role: RoleUserEnum | string): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (role === RoleUserEnum.ADMIN) {
      this.formGroup.patchValue({
        roleSupervisor: checked,
        roleAttendant: checked
      });
    } else if (role === RoleUserEnum.SUPERVISOR) {
      this.formGroup.patchValue({
        roleAdmin: false,
        roleAttendant: checked
      });
    } else if (role === RoleUserEnum.ATTENDANT) {
      this.formGroup.patchValue({
        roleAdmin: false,
        roleSupervisor: false
      });
    }
  }

  onKeydown(event: KeyboardEvent, controlName: string, role: RoleUserEnum | string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const current = this.formGroup.get(controlName)?.value;
      this.formGroup.patchValue({ [controlName]: !current });

      const clickEvent = {
        target: { checked: !current }
      } as unknown as Event;

      this.toogleRoles(clickEvent, role);
    }
  }

  removeMidia(): void {
    if (this.user.image) {
      this.firebaseService.deleteFile(this.user.image)
        .then(() => {
          this.resetFileState();
        })
        .catch(error => {
          this.alertService.error(this.t('user.form.deleteFileError'));
          this.resetFileState();
        });
    }
  }

  private resetFileState(): void {
    this.user.image = '';
  }

}
